import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, ShieldCheck, ChevronRight, FlaskConical, Flame } from 'lucide-react';

export default function LabCard({ lab, className = '' }) {
  const navigate = useNavigate();

  if (!lab) return null;

  const labId = lab.id || lab.labId;
  const labName = lab.name || lab.labName || 'Certified Pathology Lab';
  const labImage = lab.image || lab.labImage;
  const labAddress = lab.address || 'Sector 6, Nearby';
  const distanceKm = lab.distanceKm !== undefined && lab.distanceKm !== null
    ? `${typeof lab.distanceKm === 'number' ? lab.distanceKm.toFixed(1) : lab.distanceKm} KM`
    : '2.4 KM';

  const handleView = () => {
    if (labId) {
      navigate('/labdetails/' + labId, { state: { lab } });
    }
  };

  return (
    <div
      onClick={handleView}
      className={'bg-white rounded-2xl border border-slate-200/90 hover:border-red-400 p-3 shadow-2xs hover:shadow-lg transition-all flex flex-col justify-between cursor-pointer group relative overflow-hidden ' + className}
    >
      <div>
        {/* Large Clean Product-Style Logo Container (NO ZOOM/HOVER SCALE) */}
        <div className='h-36 sm:h-40 w-full rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center p-3 relative overflow-hidden'>
          
          {/* Popular Badge */}
          <div className='absolute top-2 left-2 flex items-center gap-1 bg-amber-500 text-slate-950 text-[9px] font-black px-1.5 py-0.5 rounded shadow-2xs z-10'>
            <Flame size={10} className='text-slate-950 fill-current' />
            <span>POPULAR</span>
          </div>

          <div className='absolute top-2 right-2 flex items-center gap-1 bg-black/60 backdrop-blur-xs text-white text-[9px] font-bold px-1.5 py-0.5 rounded z-10'>
            <MapPin size={9} className='text-yellow-400' />
            <span>{distanceKm}</span>
          </div>

          {/* Clean Fixed Logo */}
          {labImage ? (
            <img
              src={labImage}
              alt={labName}
              className='max-h-full max-w-full object-contain'
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = 'https://cdn-icons-png.flaticon.com/128/10801/10801900.png';
              }}
            />
          ) : (
            <FlaskConical size={38} className='text-red-500' />
          )}
        </div>

        {/* Lab Details below Image */}
        <div className='mt-3'>
          <h3 className='text-xs sm:text-sm font-extrabold text-slate-900 group-hover:text-red-600 transition-colors line-clamp-1 leading-snug'>
            {labName}
          </h3>

          <p className='text-[11px] text-slate-500 line-clamp-1 mt-0.5 flex items-center gap-1'>
            <MapPin size={10} className='text-slate-400 shrink-0' />
            <span>{labAddress}</span>
          </p>

          {/* Micro feature badge */}
          <div className='mt-2 flex items-center gap-1.5 text-[10px] text-emerald-700 bg-emerald-50 px-2 py-1 rounded font-semibold'>
            <ShieldCheck size={11} className='shrink-0' />
            <span className='truncate'>NABL Certified</span>
          </div>
        </div>
      </div>

      {/* Bottom Button */}
      <div className='mt-3 pt-2.5 border-t border-slate-100'>
        <button 
          onClick={(e) => {
            e.stopPropagation();
            handleView();
          }}
          className='w-full bg-red-50 group-hover:bg-red-600 text-red-600 group-hover:text-white font-bold text-xs py-2 px-3 rounded-xl transition-all border border-red-200 group-hover:border-red-600 flex items-center justify-center gap-1 shadow-2xs group-hover:shadow-md'
        >
          <span>View Lab</span>
          <ChevronRight size={13} />
        </button>
      </div>
    </div>
  );
}
